import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { projectsApi, Project, ProjectType, ProjectMilestone, ProjectClientContact } from '../../api/projectsApi';
import { clientsApi, Client } from '../../api/clientsApi';
import { usersApi } from '../../api/usersApi';
import { User } from '../../types';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Card } from '../../components/common/Card';
import { Loader } from '../../components/common/Loader';
import { Icons } from '../../components/common/Icons';
import { TableFilters, ColumnConfig, FilterConfig } from '../../components/common/TableFilters';
import { PhoneInput } from '../../components/common/PhoneInput';
import { downloadBlob } from '../../utils/helpers';
import './Projects.css';

const PROJECT_TYPE_LABELS: Record<string, string> = {
  adm: 'ADM',
  tnm: 'T&M',
  staff_aug: 'Staff Aug',
  inhouse: 'In-house',
  digital_marketing: 'Digital Marketing',
  upwork: 'Upwork',
  outsource: 'Outsource',
};

interface MilestoneFormItem {
  name: string;
  start_date: string;
  end_date: string;
}

interface ClientContactFormItem {
  name: string;
  email: string;
  phone: string;
}

interface ResourceFormItem {
  employee_id: number | null;
  start_date: string;
  end_date: string;
}

interface ProjectFormData {
  name: string;
  code: string;
  client_id: number | null;
  manager_id: number | null;
  sales_person_id: number | null;
  description: string;
  start_date: string;
  end_date: string;
  status: string;
  is_billable: boolean;
  project_type: ProjectType | '';
  monthly_hours: string;
  total_months: string;
  milestones: MilestoneFormItem[];
  client_contacts: ClientContactFormItem[];
  resources: ResourceFormItem[];
}

interface TeamMember {
  assignment_id: number;
  employee_id: number;
  employee_name: string;
  employee_email: string;
  assigned_at: string;
  start_date?: string | null;
  end_date?: string | null;
}

// Column configuration
const ALL_COLUMNS: ColumnConfig[] = [
  { key: 'name', label: 'Project', defaultVisible: true },
  { key: 'code', label: 'Code', defaultVisible: true },
  { key: 'client', label: 'Client', defaultVisible: true },
  { key: 'manager', label: 'Manager', defaultVisible: true },
  { key: 'type', label: 'Type', defaultVisible: true },
  { key: 'billable', label: 'Billable', defaultVisible: true },
  { key: 'status', label: 'Status', defaultVisible: true },
  { key: 'duration', label: 'Duration', defaultVisible: true },
  { key: 'description', label: 'Description', defaultVisible: false },
  { key: 'project_category', label: 'Project Category', defaultVisible: false },
  { key: 'allocated_hours', label: 'Allocated Hours', defaultVisible: false },
  { key: 'hours_spent', label: 'Hours Spent', defaultVisible: false },
  { key: 'sales_person', label: 'Sales Person', defaultVisible: false },
  { key: 'stage', label: 'Stage', defaultVisible: false },
  { key: 'tags', label: 'Tags', defaultVisible: false },
];

const DEFAULT_VISIBLE_COLUMNS = ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key);

const PER_PAGE = 50;

export function Projects() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [managers, setManagers] = useState<User[]>([]);
  const [salesPersons, setSalesPersons] = useState<Array<{ id: number; name: string; email: string; department: string | null }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  
  // Filter state - default to showing active projects only
  const [filterValues, setFilterValues] = useState<Record<string, string | number | undefined>>({ status: 'active' });
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_VISIBLE_COLUMNS);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  
  // Team management
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [availableEmployees, setAvailableEmployees] = useState<Array<{ id: number; name: string; email: string; employee_code: string }>>([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([]);
  const [allEmployees, setAllEmployees] = useState<Array<{ id: number; name: string; email: string; employee_code: string }>>([]);
  // Date inputs for team assignment
  const [assignStartDate, setAssignStartDate] = useState('');
  const [assignEndDate, setAssignEndDate] = useState('');
  // Deassign modal state
  const [deassignModal, setDeassignModal] = useState<{ employeeId: number; employeeName: string } | null>(null);
  const [deassignDate, setDeassignDate] = useState('');
  // Whether the team modal's project is Staff Aug
  const isStaffAugProject = selectedProject?.project_type === 'staff_aug';

  // Staff Aug: live client contacts + resources (for team modal)
  const [liveContacts, setLiveContacts] = useState<ProjectClientContact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [newContactName, setNewContactName] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  // Adding resource to a specific contact
  const [addResourceContactId, setAddResourceContactId] = useState<number | null>(null);
  const [newResourceEmployeeId, setNewResourceEmployeeId] = useState<number | null>(null);
  const [resourceEmployeeSearch, setResourceEmployeeSearch] = useState('');
  const [newResourceStartDate, setNewResourceStartDate] = useState('');
  const [newResourceEndDate, setNewResourceEndDate] = useState('');

  // Milestone management (for ADM projects inline on list)
  const [showMilestonesModal, setShowMilestonesModal] = useState(false);
  const [milestonesProject, setMilestonesProject] = useState<Project | null>(null);
  const [liveMilestones, setLiveMilestones] = useState<ProjectMilestone[]>([]);
  const [milestonesLoading, setMilestonesLoading] = useState(false);
  const [newMilestoneName, setNewMilestoneName] = useState('');
  const [newMilestoneStart, setNewMilestoneStart] = useState('');
  const [newMilestoneEnd, setNewMilestoneEnd] = useState('');

  const emptyFormData: ProjectFormData = {
    name: '',
    code: '',
    client_id: null,
    manager_id: null,
    sales_person_id: null,
    description: '',
    start_date: '',
    end_date: '',
    status: 'active',
    is_billable: false,
    project_type: '',
    monthly_hours: '',
    total_months: '',
    milestones: [],
    client_contacts: [],
    resources: [],
  };

  const [formData, setFormData] = useState<ProjectFormData>(emptyFormData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Pagination calculations
  const totalPages = Math.ceil(projects.length / PER_PAGE);
  const paginatedProjects = useMemo(() => {
    const start = (currentPage - 1) * PER_PAGE;
    return projects.slice(start, start + PER_PAGE);
  }, [projects, currentPage]);
  const startItem = projects.length === 0 ? 0 : (currentPage - 1) * PER_PAGE + 1;
  const endItem = Math.min(currentPage * PER_PAGE, projects.length);

  // Build filter configs from loaded data
  const filterConfigs: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'completed', label: 'Completed' },
        { value: 'on_hold', label: 'On Hold' },
        { value: 'cancelled', label: 'Cancelled' },
        { value: '', label: 'All' },
      ],
    },
    {
      key: 'client_id',
      label: 'Client',
      options: clients.map(c => ({ value: c.id, label: c.name })),
    },
    {
      key: 'manager_id',
      label: 'Manager',
      options: managers.map(m => ({ value: m.id, label: m.name })),
    },
    {
      key: 'project_type',
      label: 'Project Type',
      options: [
        { value: 'adm', label: 'ADM' },
        { value: 'tnm', label: 'T&M' },
        { value: 'staff_aug', label: 'Staff Aug' },
        { value: 'inhouse', label: 'In-House' },
      ],
    },
    {
      key: 'is_billable',
      label: 'Billable',
      options: [
        { value: 'true', label: 'Billable' },
        { value: 'false', label: 'Non-Billable' },
      ],
    },
  ];

  const fetchData = useCallback(async (filters: Record<string, string | number | undefined> = {}) => {
    try {
      setLoading(true);
      setError(null);
      const [projectsRes, clientsRes, usersRes] = await Promise.all([
        projectsApi.getProjects({
          status: filters.status as string | undefined,
          client_id: filters.client_id as number | undefined,
          manager_id: filters.manager_id as number | undefined,
          project_type: filters.project_type as string | undefined,
          is_billable: filters.is_billable as string | undefined,
          search: searchTerm || undefined,
        }),
        clientsApi.getClients(),
        usersApi.getUsers({ per_page: 100, role: 'manager', is_active: 'true' }),
      ]);
      setProjects(projectsRes.projects);
      setClients(clientsRes.clients);
      setManagers(usersRes.users);
      // Load sales persons separately
      try {
        const spRes = await usersApi.getSalesPersons();
        setSalesPersons(spRes.sales_persons);
      } catch { /* ignore */ }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  // Lazy-load all employees only when needed (form/modal)
  const ensureAllEmployeesLoaded = useCallback(async () => {
    if (allEmployees.length > 0) return;
    try {
      const res = await usersApi.getUsers({ per_page: 1000 });
      setAllEmployees(res.users.map(u => ({ id: u.id, name: u.name, email: u.email, employee_code: u.employee_code || '' })));
    } catch (err) {
      console.error('Failed to load employees', err);
    }
  }, [allEmployees.length]);

  // Initial load with default filter (active projects only)
  useEffect(() => {
    fetchData({ status: 'active' });
  }, []);

  const handleSearch = () => {
    fetchData(filterValues);
  };

  const handleFilterChange = (key: string, value: string | number | undefined) => {
    const newFilters = { ...filterValues, [key]: value };
    setFilterValues(newFilters);
    fetchData(newFilters);
  };

  const handleExport = async (format: 'csv' | 'xlsx') => {
    try {
      setExportLoading(true);
      const blob = await projectsApi.exportProjects({
        format,
        search: searchTerm || undefined,
        status: filterValues.status as string | undefined,
        client_id: filterValues.client_id as number | undefined,
        manager_id: filterValues.manager_id as number | undefined,
        columns: visibleColumns.join(','),
      });
      downloadBlob(blob, `projects.${format}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export');
    } finally {
      setExportLoading(false);
    }
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Project name is required.';
    else if (formData.name.length > 60) errors.name = 'Project name must be 60 characters or fewer.';
    if (formData.code && formData.code.length > 20) errors.code = 'Project code must be 20 characters or fewer.';
    if (!formData.client_id) errors.client_id = 'Client is required.';
    if (!formData.manager_id) errors.manager_id = 'Manager is required.';
    if (!formData.project_type) errors.project_type = 'Project type is required.';
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setFormErrors({});
    try {
      const projectType = formData.project_type || undefined;
      const res = await projectsApi.createProject({
        name: formData.name,
        code: formData.code || undefined,
        client_id: formData.client_id || undefined,
        manager_id: formData.manager_id || undefined,
        sales_person_id: formData.sales_person_id || undefined,
        description: formData.description || undefined,
        start_date: formData.start_date || undefined,
        end_date: formData.end_date || undefined,
        status: formData.status,
        is_billable: formData.is_billable,
        project_type: projectType as ProjectType | undefined,
        monthly_hours: formData.monthly_hours ? Number(formData.monthly_hours) : undefined,
        total_months: formData.total_months ? Number(formData.total_months) : undefined,
        milestones: projectType === 'adm' ? formData.milestones.filter(m => m.name.trim()) : undefined,
        client_contacts: projectType === 'staff_aug' ? formData.client_contacts.filter(c => c.name.trim()) : undefined,
        resources: projectType === 'staff_aug' ? formData.resources.filter(r => r.employee_id).map(r => ({
          employee_id: r.employee_id || undefined,
          start_date: r.start_date || undefined,
          end_date: r.end_date || undefined,
        })) : undefined,
      });
      setProjects([...projects, res.project]);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project');
    }
  };

  const handleUpdateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProject) return;
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Project name is required.';
    else if (formData.name.length > 60) errors.name = 'Project name must be 60 characters or fewer.';
    if (formData.code && formData.code.length > 20) errors.code = 'Project code must be 20 characters or fewer.';
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setFormErrors({});
    try {
      const projectType = formData.project_type || undefined;
      const res = await projectsApi.updateProject(editingProject.id, {
        name: formData.name,
        code: formData.code || undefined,
        client_id: formData.client_id || undefined,
        manager_id: formData.manager_id || undefined,
        sales_person_id: formData.sales_person_id || undefined,
        description: formData.description || undefined,
        start_date: formData.start_date || undefined,
        end_date: formData.end_date || undefined,
        status: formData.status,
        is_billable: formData.is_billable,
        project_type: projectType as ProjectType | undefined,
        monthly_hours: formData.monthly_hours ? Number(formData.monthly_hours) : undefined,
        total_months: formData.total_months ? Number(formData.total_months) : undefined,
      });
      setProjects(projects.map(p => p.id === editingProject.id ? res.project : p));
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update project');
    }
  };

  const openEditModal = (project: Project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      code: project.code || '',
      client_id: project.client_id,
      manager_id: project.manager_id,
      sales_person_id: project.sales_person_id,
      description: project.description || '',
      start_date: project.start_date || '',
      end_date: project.end_date || '',
      status: project.status,
      is_billable: project.is_billable || false,
      project_type: project.project_type || '',
      monthly_hours: project.monthly_hours ? String(project.monthly_hours) : '',
      total_months: project.total_months ? String(project.total_months) : '',
      milestones: [],
      client_contacts: [],
      resources: [],
    });
    setShowModal(true);
    ensureAllEmployeesLoaded();
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingProject(null);
    setFormData(emptyFormData);
    setFormErrors({});
  };

  // Team Management Functions
  const openTeamModal = async (project: Project) => {
    setSelectedProject(project);
    setShowTeamModal(true);
    setTeamLoading(true);
    setSelectedEmployeeIds([]);
    setEmployeeSearch('');
    setAssignStartDate('');
    setAssignEndDate('');
    setDeassignModal(null);
    setDeassignDate('');
    // Reset Staff Aug state
    setLiveContacts([]);
    setNewContactName('');
    setNewContactEmail('');
    setNewContactPhone('');
    setAddResourceContactId(null);
    
    // Employees are loaded below via getEmployeesForAssignment
    
    try {
      if (project.project_type === 'staff_aug') {
        // Load client contacts with their nested resources
        const [contactsRes, employeesRes] = await Promise.all([
          projectsApi.getClientContacts(project.id),
          usersApi.getEmployeesForAssignment({}),
        ]);
        setLiveContacts(contactsRes.client_contacts);
        setAllEmployees(employeesRes.employees);
      } else {
        const [teamRes, employeesRes] = await Promise.all([
          projectsApi.getProjectTeam(project.id),
          usersApi.getEmployeesForAssignment({ exclude_project_id: project.id }),
        ]);
        setTeamMembers(teamRes.team);
        setAvailableEmployees(employeesRes.employees);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load team');
    } finally {
      setTeamLoading(false);
    }
  };

  const openDeassignModal = (employeeId: number, employeeName: string) => {
    setDeassignModal({ employeeId, employeeName });
    // Default to today's date
    setDeassignDate(new Date().toISOString().split('T')[0]);
  };

  const handleDeassign = async () => {
    if (!selectedProject || !deassignModal) return;
    
    try {
      setTeamLoading(true);
      await projectsApi.deassignEmployee(selectedProject.id, deassignModal.employeeId, deassignDate || undefined);
      setTeamMembers(teamMembers.filter(m => m.employee_id !== deassignModal.employeeId));
      // Refresh available employees
      const employeesRes = await usersApi.getEmployeesForAssignment({ exclude_project_id: selectedProject.id });
      setAvailableEmployees(employeesRes.employees);
      setDeassignModal(null);
      setDeassignDate('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deassign employee');
    } finally {
      setTeamLoading(false);
    }
  };

  const handleAddToTeam = async () => {
    if (!selectedProject || selectedEmployeeIds.length === 0) return;

    // For Staff Aug projects, require dates
    if (isStaffAugProject && (!assignStartDate || !assignEndDate)) {
      setError('Start date and end date are required for Staff Augmentation projects');
      return;
    }
    
    try {
      setTeamLoading(true);
      const result = await projectsApi.bulkAssignEmployees(
        selectedProject.id,
        selectedEmployeeIds,
        assignStartDate || undefined,
        assignEndDate || undefined
      );
      
      // Refresh team list
      const teamRes = await projectsApi.getProjectTeam(selectedProject.id);
      setTeamMembers(teamRes.team);
      
      // Refresh available employees
      const employeesRes = await usersApi.getEmployeesForAssignment({ exclude_project_id: selectedProject.id });
      setAvailableEmployees(employeesRes.employees);
      
      setSelectedEmployeeIds([]);
      
      if (result.failed > 0) {
        setError(`${result.successful} assigned, ${result.failed} failed`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign employees');
    } finally {
      setTeamLoading(false);
    }
  };

  const searchEmployees = async () => {
    if (!selectedProject) return;
    try {
      const res = await usersApi.getEmployeesForAssignment({
        search: employeeSearch,
        exclude_project_id: selectedProject.id,
      });
      setAvailableEmployees(res.employees);
    } catch (err) {
      console.error('Failed to search employees', err);
    }
  };

  const toggleEmployeeSelection = (employeeId: number) => {
    setSelectedEmployeeIds(prev => 
      prev.includes(employeeId) 
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  // Milestone modal functions
  const openMilestonesModal = async (project: Project) => {
    setMilestonesProject(project);
    setShowMilestonesModal(true);
    setMilestonesLoading(true);
    try {
      const res = await projectsApi.getMilestones(project.id);
      setLiveMilestones(res.milestones);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load milestones');
    } finally {
      setMilestonesLoading(false);
    }
  };

  const handleAddLiveMilestone = async () => {
    if (!milestonesProject || !newMilestoneName.trim()) return;
    try {
      const res = await projectsApi.createMilestone(milestonesProject.id, {
        name: newMilestoneName.trim(),
        start_date: newMilestoneStart || undefined,
        end_date: newMilestoneEnd || undefined,
      });
      setLiveMilestones([...liveMilestones, res.milestone]);
      setNewMilestoneName('');
      setNewMilestoneStart('');
      setNewMilestoneEnd('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add milestone');
    }
  };

  const handleToggleMilestoneComplete = async (milestone: ProjectMilestone) => {
    if (!milestonesProject) return;
    try {
      const res = await projectsApi.completeMilestone(milestonesProject.id, milestone.id);
      setLiveMilestones(liveMilestones.map(m => m.id === milestone.id ? res.milestone : m));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update milestone');
    }
  };

  const handleDeleteLiveMilestone = async (milestoneId: number) => {
    if (!milestonesProject || !window.confirm('Delete this milestone?')) return;
    try {
      await projectsApi.deleteMilestone(milestonesProject.id, milestoneId);
      setLiveMilestones(liveMilestones.filter(m => m.id !== milestoneId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete milestone');
    }
  };

  // Form helper: add/remove dynamic rows
  const addMilestoneRow = () => {
    setFormData({ ...formData, milestones: [...formData.milestones, { name: '', start_date: '', end_date: '' }] });
  };
  const removeMilestoneRow = (index: number) => {
    setFormData({ ...formData, milestones: formData.milestones.filter((_, i) => i !== index) });
  };
  const addContactRow = () => {
    setFormData({ ...formData, client_contacts: [...formData.client_contacts, { name: '', email: '', phone: '' }] });
  };
  const removeContactRow = (index: number) => {
    setFormData({ ...formData, client_contacts: formData.client_contacts.filter((_, i) => i !== index) });
  };
  const addResourceRow = () => {
    setFormData({ ...formData, resources: [...formData.resources, { employee_id: null, start_date: '', end_date: '' }] });
  };
  const removeResourceRow = (index: number) => {
    setFormData({ ...formData, resources: formData.resources.filter((_, i) => i !== index) });
  };

  // ===== Staff Aug: Live Client Contact & Resource Management (in team modal) =====

  const handleAddLiveContact = async () => {
    if (!selectedProject || !newContactName.trim()) return;
    try {
      setContactsLoading(true);
      const res = await projectsApi.createClientContact(selectedProject.id, {
        name: newContactName.trim(),
        email: newContactEmail.trim() || undefined,
        phone: newContactPhone.trim() || undefined,
      });
      setLiveContacts([...liveContacts, { ...res.client_contact, resources: [] }]);
      setNewContactName('');
      setNewContactEmail('');
      setNewContactPhone('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add client contact');
    } finally {
      setContactsLoading(false);
    }
  };

  const handleDeleteLiveContact = async (contactId: number) => {
    if (!selectedProject || !window.confirm('Delete this client contact and all its resources?')) return;
    try {
      setContactsLoading(true);
      await projectsApi.deleteClientContact(selectedProject.id, contactId);
      setLiveContacts(liveContacts.filter(c => c.id !== contactId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete client contact');
    } finally {
      setContactsLoading(false);
    }
  };

  const handleAddResourceToContact = async (contactId: number) => {
    if (!selectedProject || !newResourceEmployeeId) return;
    try {
      setContactsLoading(true);
      const res = await projectsApi.createResource(selectedProject.id, {
        client_contact_id: contactId,
        employee_id: newResourceEmployeeId,
        start_date: newResourceStartDate || undefined,
        end_date: newResourceEndDate || undefined,
      });
      // Update local state: add resource into the matching contact
      setLiveContacts(liveContacts.map(c => {
        if (c.id === contactId) {
          return { ...c, resources: [...(c.resources || []), res.resource] };
        }
        return c;
      }));
      setAddResourceContactId(null);
      setNewResourceEmployeeId(null);
      setResourceEmployeeSearch('');
      setNewResourceStartDate('');
      setNewResourceEndDate('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add resource');
    } finally {
      setContactsLoading(false);
    }
  };

  const handleDeleteResource = async (contactId: number, resourceId: number) => {
    if (!selectedProject) return;
    try {
      setContactsLoading(true);
      await projectsApi.deleteResource(selectedProject.id, resourceId);
      setLiveContacts(liveContacts.map(c => {
        if (c.id === contactId) {
          return { ...c, resources: (c.resources || []).filter(r => r.id !== resourceId) };
        }
        return c;
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete resource');
    } finally {
      setContactsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'status-active';
      case 'completed': return 'status-completed';
      case 'on_hold': return 'status-on-hold';
      case 'cancelled': return 'status-cancelled';
      default: return '';
    }
  };

  const renderProjectRow = (project: Project, cols: string[]) => (
    <tr key={project.id}>
      {cols.includes('name') && (
        <td className="project-name"><span className="name">{project.name}</span></td>
      )}
      {cols.includes('code') && <td>{project.code || '-'}</td>}
      {cols.includes('client') && <td>{project.client_name || '-'}</td>}
      {cols.includes('manager') && <td>{project.manager_name || '-'}</td>}
      {cols.includes('type') && (
        <td>
          {project.project_type ? (
            <span className={`type-badge type-${project.project_type}`}>
              {PROJECT_TYPE_LABELS[project.project_type] || project.project_type}
            </span>
          ) : '-'}
        </td>
      )}
      {cols.includes('billable') && (
        <td>
          <span className={`billable-badge ${project.is_billable ? 'billable-yes' : 'billable-no'}`}>
            {project.is_billable ? 'Yes' : 'No'}
          </span>
        </td>
      )}
      {cols.includes('status') && (
        <td>
          <span className={`status-badge ${getStatusColor(project.status)}`}>
            {project.status.replace('_', ' ')}
          </span>
        </td>
      )}
      {cols.includes('duration') && (
        <td className="duration">
          {project.start_date && project.end_date
            ? `${project.start_date} - ${project.end_date}`
            : project.start_date || project.end_date || '-'}
        </td>
      )}
      {cols.includes('description') && <td>{project.description || '-'}</td>}
      <td>
        <div className="action-buttons">
          {project.project_type === 'adm' && (
            <button className="action-btn milestone" onClick={() => openMilestonesModal(project)} title="Milestones">
              <Icons.Layers />
            </button>
          )}
          <button className="action-btn team" onClick={() => openTeamModal(project)} title="Manage Team">
            <Icons.Users />
          </button>
          <button className="action-btn edit" onClick={() => openEditModal(project)} title="Edit">
            <Icons.Edit />
          </button>
        </div>
      </td>
    </tr>
  );

  return (
    <div className="projects-page">
      <div className="projects-header">
        <div className="projects-title">
          <h1>Project Management</h1>
          <p>Manage all projects and team assignments</p>
        </div>
        <Button onClick={() => { setShowModal(true); ensureAllEmployeesLoaded(); }}>
          <Icons.Plus /> Add Project
        </Button>
      </div>

      {error && (
        <div className="error-banner">
          <Icons.AlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      <Card>
        <TableFilters
          searchPlaceholder="Search projects..."
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          onSearch={handleSearch}
          filters={filterConfigs}
          filterValues={filterValues}
          onFilterChange={handleFilterChange}
          columns={ALL_COLUMNS}
          visibleColumns={visibleColumns}
          onColumnToggle={setVisibleColumns}
          onExport={handleExport}
          exportLoading={exportLoading}
          onRefresh={() => fetchData(filterValues)}
          refreshLoading={loading}
          totalCount={projects.length}
          shownCount={paginatedProjects.length}
          countLabel="Projects"
        />

        <div className="projects-table-wrapper">
          <table className="projects-table">
              <thead>
                <tr>
                  {visibleColumns.includes('name') && <th>Project</th>}
                  {visibleColumns.includes('code') && <th>Code</th>}
                  {visibleColumns.includes('client') && <th>Client</th>}
                  {visibleColumns.includes('manager') && <th>Manager</th>}
                  {visibleColumns.includes('type') && <th>Type</th>}
                  {visibleColumns.includes('billable') && <th>Billable</th>}
                  {visibleColumns.includes('status') && <th>Status</th>}
                  {visibleColumns.includes('duration') && <th>Duration</th>}
                  {visibleColumns.includes('description') && <th>Description</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={visibleColumns.length + 1} className="empty-state"><Loader /></td></tr>
                ) : paginatedProjects.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length + 1} className="empty-state">
                      <Icons.Folder />
                      <p>No projects found</p>
                    </td>
                  </tr>
                ) : (
                  paginatedProjects.map((project) => renderProjectRow(project, visibleColumns))
                )}
              </tbody>
            </table>
        </div>

        {/* Pagination */}
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {projects.length > 0 ? startItem : 0} to {endItem} of {projects.length} projects
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              title="First page"
            >
              <Icons.ChevronsLeft />
            </button>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              title="Previous page"
            >
              <Icons.ChevronLeft />
            </button>

            <div className="pagination-pages">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    className={`pagination-btn page-num ${currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              title="Next page"
            >
              <Icons.ChevronRight />
            </button>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              title="Last page"
            >
              <Icons.ChevronsRight />
            </button>
          </div>
        </div>
      </Card>

      {/* Create/Edit Project Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProject ? 'Edit Project' : 'Add New Project'}</h2>
              <button className="modal-close" onClick={resetForm}>
                <Icons.X />
              </button>
            </div>
            <form onSubmit={editingProject ? handleUpdateProject : handleCreateProject}>
              <div className="form-row">
                <div className="form-group">
                  <label>Project Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter project name"
                    maxLength={60}
                  />
                  {formErrors.name && <span className="field-error">{formErrors.name}</span>}
                </div>
                <div className="form-group">
                  <label>Project Code</label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="e.g., PRJ-001"
                    maxLength={20}
                  />
                  {formErrors.code && <span className="field-error">{formErrors.code}</span>}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Client *</label>
                  <select
                    value={formData.client_id || ''}
                    onChange={(e) => setFormData({ ...formData, client_id: e.target.value ? Number(e.target.value) : null })}
                    className="form-select"
                  >
                    <option value="">Select Client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                  </select>
                  {formErrors.client_id && <span className="field-error">{formErrors.client_id}</span>}
                </div>
                <div className="form-group">
                  <label>Manager *</label>
                  <select
                    value={formData.manager_id || ''}
                    onChange={(e) => setFormData({ ...formData, manager_id: e.target.value ? Number(e.target.value) : null })}
                    className="form-select"
                  >
                    <option value="">Select Manager</option>
                    {managers.map(manager => (
                      <option key={manager.id} value={manager.id}>{manager.name}</option>
                    ))}
                  </select>
                  {formErrors.manager_id && <span className="field-error">{formErrors.manager_id}</span>}
                </div>
              </div>

              {/* Sales Person row */}
              <div className="form-row">
                <div className="form-group">
                  <label>Sales Person</label>
                  <select
                    value={formData.sales_person_id || ''}
                    onChange={(e) => setFormData({ ...formData, sales_person_id: e.target.value ? Number(e.target.value) : null })}
                    className="form-select"
                  >
                    <option value="">Select Sales Person</option>
                    {salesPersons.map(sp => (
                      <option key={sp.id} value={sp.id}>{sp.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Billable + Project Type row */}
              <div className="form-row">
                <div className="form-group">
                  <label>Billable</label>
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={formData.is_billable}
                      onChange={(e) => setFormData({ ...formData, is_billable: e.target.checked })}
                    />
                    <span className="toggle-text">{formData.is_billable ? 'Yes' : 'No'}</span>
                  </label>
                </div>
                <div className="form-group">
                  <label>Project Type *</label>
                  <select
                    value={formData.project_type}
                    onChange={(e) => setFormData({
                      ...formData,
                      project_type: e.target.value as ProjectType | '',
                      milestones: e.target.value === 'adm' ? formData.milestones : [],
                      client_contacts: e.target.value === 'staff_aug' ? formData.client_contacts : [],
                      resources: e.target.value === 'staff_aug' ? formData.resources : [],
                      monthly_hours: e.target.value === 'tnm' ? formData.monthly_hours : '',
                      total_months: e.target.value === 'tnm' ? formData.total_months : '',
                    })}
                    className="form-select"
                  >
                    <option value="">Select Type</option>
                    <option value="adm">ADM</option>
                    <option value="tnm">T&M</option>
                    <option value="staff_aug">Staff Augmentation</option>
                    <option value="inhouse">In-house</option>
                  </select>
                  {formErrors.project_type && <span className="field-error">{formErrors.project_type}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="form-select"
                  >
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="on_hold">On Hold</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              {/* T&M: Monthly Hours + Total Months */}
              {formData.project_type === 'tnm' && (
                <div className="type-section">
                  <h3 className="type-section-title">T&M Configuration</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Monthly Hours</label>
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        value={formData.monthly_hours}
                        onChange={(e) => setFormData({ ...formData, monthly_hours: e.target.value })}
                        placeholder="e.g., 160"
                      />
                    </div>
                    <div className="form-group">
                      <label>Total Months</label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.total_months}
                        onChange={(e) => setFormData({ ...formData, total_months: e.target.value })}
                        placeholder="e.g., 6"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* ADM: Milestones (only during creation) */}
              {formData.project_type === 'adm' && !editingProject && (
                <div className="type-section">
                  <div className="type-section-header">
                    <h3 className="type-section-title">Milestones</h3>
                    <Button type="button" variant="secondary" size="sm" onClick={addMilestoneRow}>
                      + Add Milestone
                    </Button>
                  </div>
                  {formData.milestones.length === 0 ? (
                    <p className="empty-text">No milestones added yet. You can also add them after creation.</p>
                  ) : (
                    formData.milestones.map((ms, idx) => (
                      <div key={idx} className="dynamic-row">
                        <Input
                          value={ms.name}
                          onChange={(e) => {
                            const updated = [...formData.milestones];
                            updated[idx] = { ...updated[idx], name: e.target.value };
                            setFormData({ ...formData, milestones: updated });
                          }}
                          placeholder="Milestone name"
                        />
                        <Input
                          type="date"
                          value={ms.start_date}
                          onChange={(e) => {
                            const updated = [...formData.milestones];
                            updated[idx] = { ...updated[idx], start_date: e.target.value };
                            setFormData({ ...formData, milestones: updated });
                          }}
                        />
                        <Input
                          type="date"
                          value={ms.end_date}
                          onChange={(e) => {
                            const updated = [...formData.milestones];
                            updated[idx] = { ...updated[idx], end_date: e.target.value };
                            setFormData({ ...formData, milestones: updated });
                          }}
                        />
                        <button type="button" className="remove-row-btn" onClick={() => removeMilestoneRow(idx)} title="Remove">
                          &times;
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Staff Aug: Client contacts & resources are managed via the Team (👥) button after project creation */}
              {formData.project_type === 'staff_aug' && !editingProject && (
                <div className="type-section">
                  <p className="empty-text">After creating the project, use the <strong>👥 Team</strong> button to add Client Contacts and assign Resources (employees) under each contact.</p>
                </div>
              )}
              {formData.project_type === 'staff_aug' && editingProject && (
                <div className="type-section">
                  <p className="empty-text">Manage Client Contacts and Resources via the <strong>👥 Team</strong> button on the project row.</p>
                </div>
              )}

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the project"
                  rows={3}
                />
              </div>
              <div className="modal-actions">
                <Button type="button" variant="secondary" onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Saving...' : (editingProject ? 'Update Project' : 'Create Project')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Team Management Modal */}
      {showTeamModal && selectedProject && (
        <div className="modal-overlay" onClick={() => { setShowTeamModal(false); setDeassignModal(null); }}>
          <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{isStaffAugProject ? 'Clients & Resources' : 'Team'}: {selectedProject.name}</h2>
              <button className="modal-close" onClick={() => { setShowTeamModal(false); setDeassignModal(null); }}>
                <Icons.X />
              </button>
            </div>
            <div className="team-modal-content">
              {teamLoading ? (
                <Loader text="Loading..." />
              ) : isStaffAugProject ? (
                /* ===== STAFF AUG: Client Contacts + nested Resources ===== */
                <>
                  {liveContacts.length === 0 && (
                    <p className="empty-text">No client contacts added yet. Add a client contact below.</p>
                  )}
                  <div className="contacts-list">
                    {liveContacts.map(contact => (
                      <div key={contact.id} className="contact-card">
                        <div className="contact-header">
                          <div className="contact-info">
                            <span className="contact-name">{contact.name}</span>
                            {contact.email && <span className="contact-email">{contact.email}</span>}
                            {contact.phone && <span className="contact-phone">{contact.phone}</span>}
                          </div>
                          <button
                            className="action-btn delete"
                            onClick={() => handleDeleteLiveContact(contact.id)}
                            title="Delete contact"
                            disabled={contactsLoading}
                          >
                            <Icons.Trash />
                          </button>
                        </div>

                        {/* Resources under this contact */}
                        <div className="contact-resources">
                          <div className="resources-header">
                            <span className="resources-label">Resources ({(contact.resources || []).length})</span>
                            <button
                              type="button"
                              className="add-resource-inline-btn"
                              onClick={() => {
                                setAddResourceContactId(addResourceContactId === contact.id ? null : contact.id);
                                setNewResourceEmployeeId(null);
                                setResourceEmployeeSearch('');
                                setNewResourceStartDate('');
                                setNewResourceEndDate('');
                              }}
                            >
                              {addResourceContactId === contact.id ? '✕ Cancel' : '+ Add Resource'}
                            </button>
                          </div>

                          {(contact.resources || []).length === 0 ? (
                            <p className="empty-text small">No resources assigned</p>
                          ) : (
                            <div className="resources-list">
                              {(contact.resources || []).map(res => (
                                <div key={res.id} className="resource-item">
                                  <div className="resource-info">
                                    <span className="resource-name">{res.employee_name || 'Unassigned'}</span>
                                    {(res.start_date || res.end_date) && (
                                      <span className="resource-dates">
                                        {res.start_date || '—'} → {res.end_date || 'Ongoing'}
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    className="action-btn delete"
                                    onClick={() => handleDeleteResource(contact.id, res.id)}
                                    title="Remove resource"
                                    disabled={contactsLoading}
                                  >
                                    <Icons.Trash />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Inline add resource form */}
                          {addResourceContactId === contact.id && (
                            <div className="add-resource-form">
                              <div className="form-group">
                                <label>Employee *</label>
                                <div className="resource-employee-picker">
                                  <input
                                    type="text"
                                    className="form-input"
                                    placeholder="Search employee..."
                                    value={newResourceEmployeeId
                                      ? (allEmployees.find(e => e.id === newResourceEmployeeId)?.name + ' (' + allEmployees.find(e => e.id === newResourceEmployeeId)?.employee_code + ')') || ''
                                      : resourceEmployeeSearch}
                                    onChange={(e) => { setResourceEmployeeSearch(e.target.value); setNewResourceEmployeeId(null); }}
                                    onFocus={() => { if (newResourceEmployeeId) { setResourceEmployeeSearch(''); setNewResourceEmployeeId(null); } }}
                                    autoComplete="off"
                                  />
                                  {!newResourceEmployeeId && (
                                    <div className="resource-employee-dropdown">
                                      {allEmployees
                                        .filter(emp =>
                                          !resourceEmployeeSearch ||
                                          emp.name.toLowerCase().includes(resourceEmployeeSearch.toLowerCase()) ||
                                          emp.employee_code.toLowerCase().includes(resourceEmployeeSearch.toLowerCase())
                                        )
                                        .map(emp => (
                                          <div
                                            key={emp.id}
                                            className="resource-employee-option"
                                            onMouseDown={(e) => { e.preventDefault(); setNewResourceEmployeeId(emp.id); setResourceEmployeeSearch(''); }}
                                          >
                                            {emp.name} ({emp.employee_code})
                                          </div>
                                        ))
                                      }
                                      {allEmployees.filter(emp =>
                                        !resourceEmployeeSearch ||
                                        emp.name.toLowerCase().includes(resourceEmployeeSearch.toLowerCase()) ||
                                        emp.employee_code.toLowerCase().includes(resourceEmployeeSearch.toLowerCase())
                                      ).length === 0 && (
                                        <div className="resource-employee-empty">No employees found</div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="form-row">
                                <div className="form-group">
                                  <label>Start Date</label>
                                  <input type="date" value={newResourceStartDate} onChange={(e) => setNewResourceStartDate(e.target.value)} className="form-input" />
                                </div>
                                <div className="form-group">
                                  <label>End Date</label>
                                  <input type="date" value={newResourceEndDate} onChange={(e) => setNewResourceEndDate(e.target.value)} className="form-input" />
                                </div>
                              </div>
                              <Button
                                size="sm"
                                onClick={() => handleAddResourceToContact(contact.id)}
                                disabled={!newResourceEmployeeId || contactsLoading}
                              >
                                {contactsLoading ? 'Adding...' : 'Add Resource'}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Add new client contact */}
                  <div className="team-section add-section">
                    <h3>Add Client Contact</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Name *</label>
                        <input type="text" value={newContactName} onChange={(e) => setNewContactName(e.target.value)} className="form-input" placeholder="Contact name" />
                      </div>
                      <div className="form-group">
                        <label>Email</label>
                        <input type="email" value={newContactEmail} onChange={(e) => setNewContactEmail(e.target.value)} className="form-input" placeholder="Email" />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Phone</label>
                        <PhoneInput
                          value={newContactPhone}
                          onChange={(val) => setNewContactPhone(val)}
                        />
                      </div>
                      <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <Button onClick={handleAddLiveContact} disabled={!newContactName.trim() || contactsLoading}>
                          {contactsLoading ? 'Adding...' : 'Add Contact'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                /* ===== REGULAR PROJECT: Team members list ===== */
                <>
                  {/* Current Team Members */}
                  <div className="team-section">
                    <h3>Current Team ({teamMembers.length})</h3>
                    {teamMembers.length === 0 ? (
                      <p className="empty-text">No team members assigned yet</p>
                    ) : (
                      <div className="team-list">
                        {teamMembers.map(member => (
                          <div key={member.employee_id} className="team-member">
                            <div className="member-info">
                              <span className="member-name">{member.employee_name}</span>
                              <span className="member-email">{member.employee_email}</span>
                              {(member.start_date || member.end_date) && (
                                <span className="member-dates">
                                  {member.start_date || '—'} → {member.end_date || 'Ongoing'}
                                </span>
                              )}
                            </div>
                            <button
                              className="deassign-btn"
                              onClick={() => openDeassignModal(member.employee_id, member.employee_name)}
                              title="Deassign from project"
                            >
                              <Icons.UserMinus />
                              <span>Deassign</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Add Team Members */}
                  <div className="team-section add-section">
                    <h3>Add Team Members</h3>
                    <div className="assign-dates-section">
                      <div className="form-row">
                        <div className="form-group">
                          <label>Start Date</label>
                          <input type="date" value={assignStartDate} onChange={(e) => setAssignStartDate(e.target.value)} className="form-input" />
                        </div>
                        <div className="form-group">
                          <label>End Date</label>
                          <input type="date" value={assignEndDate} onChange={(e) => setAssignEndDate(e.target.value)} className="form-input" />
                        </div>
                      </div>
                    </div>
                    <div className="employee-search">
                      <div className="search-input-wrapper">
                        <Icons.Search className="search-icon" />
                        <input
                          type="text"
                          placeholder="Search employees..."
                          value={employeeSearch}
                          onChange={(e) => setEmployeeSearch(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && searchEmployees()}
                        />
                      </div>
                      <Button variant="secondary" size="sm" onClick={searchEmployees}>
                        Search
                      </Button>
                    </div>
                    <div className="available-employees">
                      {availableEmployees.length === 0 ? (
                        <p className="empty-text">No available employees found</p>
                      ) : (
                        availableEmployees.map(emp => (
                          <label key={emp.id} className="employee-checkbox">
                            <input
                              type="checkbox"
                              checked={selectedEmployeeIds.includes(emp.id)}
                              onChange={() => toggleEmployeeSelection(emp.id)}
                            />
                            <span className="employee-info">
                              <span className="emp-name">{emp.name}</span>
                              <span className="emp-email">{emp.email}</span>
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                    {selectedEmployeeIds.length > 0 && (
                      <div className="add-actions">
                        <Button onClick={handleAddToTeam} disabled={teamLoading}>
                          Add {selectedEmployeeIds.length} Employee{selectedEmployeeIds.length > 1 ? 's' : ''}
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Deassign Confirmation Modal */}
      {deassignModal && (
        <div className="modal-overlay" onClick={() => setDeassignModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Deassign Employee</h2>
              <button className="modal-close" onClick={() => setDeassignModal(null)}>
                <Icons.X />
              </button>
            </div>
            <div className="deassign-modal-body">
              <p>
                Remove <strong>{deassignModal.employeeName}</strong> from this project?
                The end date will be recorded for this assignment.
              </p>
              <div className="form-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={deassignDate}
                  onChange={(e) => setDeassignDate(e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="modal-actions">
                <Button variant="secondary" onClick={() => setDeassignModal(null)}>
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleDeassign} disabled={teamLoading}>
                  {teamLoading ? 'Processing...' : 'Deassign'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Milestones Modal (ADM projects) */}
      {showMilestonesModal && milestonesProject && (
        <div className="modal-overlay" onClick={() => setShowMilestonesModal(false)}>
          <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Milestones: {milestonesProject.name}</h2>
              <button className="modal-close" onClick={() => setShowMilestonesModal(false)}>
                <Icons.X />
              </button>
            </div>
            <div className="milestones-modal-content">
              {milestonesLoading ? (
                <Loader text="Loading milestones..." />
              ) : (
                <>
                  {/* Existing milestones */}
                  {liveMilestones.length === 0 ? (
                    <p className="empty-text">No milestones yet. Add one below.</p>
                  ) : (
                    <div className="milestones-list">
                      {liveMilestones.map(ms => (
                        <div
                          key={ms.id}
                          className={`milestone-item ${ms.is_completed ? 'milestone-completed' : ''} ${ms.is_overdue ? 'milestone-overdue' : ''}`}
                        >
                          <div className="milestone-info">
                            <div className="milestone-name-row">
                              <span className={`milestone-name ${ms.is_completed ? 'line-through' : ''}`}>
                                {ms.name}
                              </span>
                              {ms.is_overdue && (
                                <span className="overdue-badge">OVERDUE</span>
                              )}
                              {ms.is_completed && (
                                <span className="completed-badge">COMPLETED</span>
                              )}
                            </div>
                            <span className="milestone-dates">
                              {ms.start_date || '—'} → {ms.end_date || '—'}
                              {ms.completed_at && (
                                <span className="completed-info"> (completed {new Date(ms.completed_at).toLocaleDateString()} by {ms.completed_by_name})</span>
                              )}
                            </span>
                          </div>
                          <div className="milestone-actions">
                            <button
                              className={`action-btn ${ms.is_completed ? 'reopen' : 'complete'}`}
                              onClick={() => handleToggleMilestoneComplete(ms)}
                              title={ms.is_completed ? 'Reopen' : 'Mark Complete'}
                            >
                              {ms.is_completed ? <Icons.Refresh /> : <Icons.Check />}
                            </button>
                            <button
                              className="action-btn delete"
                              onClick={() => handleDeleteLiveMilestone(ms.id)}
                              title="Delete"
                            >
                              <Icons.Trash />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add new milestone */}
                  <div className="add-milestone-section">
                    <h3>Add Milestone</h3>
                    <div className="dynamic-row">
                      <Input
                        value={newMilestoneName}
                        onChange={(e) => setNewMilestoneName(e.target.value)}
                        placeholder="Milestone name"
                      />
                      <Input
                        type="date"
                        value={newMilestoneStart}
                        onChange={(e) => setNewMilestoneStart(e.target.value)}
                      />
                      <Input
                        type="date"
                        value={newMilestoneEnd}
                        onChange={(e) => setNewMilestoneEnd(e.target.value)}
                      />
                      <Button
                        onClick={handleAddLiveMilestone}
                        disabled={!newMilestoneName.trim()}
                      >
                        Add
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Projects;
